import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { ReportInfo } from '@blisspointmedia/bpm-types/dist/Reporting';
import { Danger300, Success300, Warning300, shadeColor } from '@src/utils/colors';
import { SIZE_ALERT_THRESHOLD } from './reporting-constants';

// STARTED has no semantic equivalent in colors.ts (no "info"/blue color in the palette) — kept as
// a literal per the "colors with no palette equivalent" exception in CLAUDE.md, lightened to
// match the softer Success/Danger/Warning tones used for the other statuses.
const STARTED_BLUE = shadeColor('#6F9FD8', 0.4);

export const colorForStatus = (status: string): string => {
  switch (status) {
    case 'SUCCESS':
      return Success300;
    case 'FAILURE':
      return Danger300;
    case 'STARTED':
      return STARTED_BLUE;
    case 'WAITING':
      return Warning300;
    default:
      return 'white';
  }
};

export const toPrettyDate = R.pipe(Dfns.parseISO, Dfns.format('M/d/yyyy @ h:mm:ss a'));

// Shared red/green health indicator used for the "is this run late" and "are any uploaded files
// too small" badges — both were the same redOrange/magicMintGreen boolean choice in bpm-frontend.
export const healthColor = (isBad: boolean): string => (isBad ? Danger300 : Success300);

// Groups reports by name, flags the latest run of each name as "late" if it took more than
// 30 minutes longer than that name's average completion time, and flags individual uploaded
// files as "too small" if they're under 95% of that file's historical average size. When
// `expandedType` is set, also includes every historical run of that one report name (not just
// its latest), so the "magnifying glass" search icon can show a name's full run history.
export const computeVisibleLines = (
  data: ReportInfo[] | undefined,
  filter: (row: ReportInfo) => boolean,
  expandedType: string | undefined
): ReportInfo[] => {
  if (!data) return [];

  const visible = R.filter(filter, data);
  const allByName = R.groupBy(row => row.name, data);

  const avgCompletionTimeByName = R.mapObjIndexed(list => {
    const finishedReports = R.filter(row => Boolean(row.completed), list);
    const minutes = R.map(row => {
      const d = row.completed ? Dfns.parseISO(row.completed) : new Date(0);
      return Dfns.getHours(d) * 60 + Dfns.getMinutes(d);
    }, finishedReports);
    return R.sum(minutes) / minutes.length;
  }, allByName);

  const fileSizeByName: Record<string, Record<string, number[]>> = {};
  for (const row of data) {
    if (!fileSizeByName[row.name]) {
      fileSizeByName[row.name] = {};
    }
    for (const file of row.files ?? []) {
      if (!file.success) continue;
      if (!fileSizeByName[row.name][file.path]) {
        fileSizeByName[row.name][file.path] = [];
      }
      fileSizeByName[row.name][file.path].push(file.size);
    }
  }

  const avgFileSizeByName: Record<string, Record<string, number>> = {};
  for (const name of R.keys(fileSizeByName)) {
    avgFileSizeByName[name] = {};
    for (const path of R.keys(fileSizeByName[name])) {
      avgFileSizeByName[name][path] =
        R.sum(fileSizeByName[name][path]) / fileSizeByName[name][path].length;
    }
  }

  const withComputedFields = visible.map(row => {
    const d = row.completed ? Dfns.parseISO(row.completed) : new Date();
    const minutes = Dfns.getHours(d) * 60 + Dfns.getMinutes(d);
    const isLate = minutes > avgCompletionTimeByName[row.name] + 30;
    const files = (row.files ?? []).map(file => {
      const avgSize = avgFileSizeByName[row.name]?.[file.path];
      return {
        ...file,
        is_too_small: Boolean(avgSize) && file.size < avgSize * SIZE_ALERT_THRESHOLD
      };
    });
    return { ...row, is_late: isLate, files };
  });

  const byName = R.groupBy(row => row.name, withComputedFields);
  const latest = R.map(
    list => R.sortBy(row => row.created, list)[list.length - 1],
    R.values(byName)
  );

  if (expandedType) {
    return R.uniq(
      R.concat(
        R.reverse(R.sortBy(row => row.created, byName[expandedType] ?? [])),
        R.reverse(R.sortBy(row => row.created, latest))
      )
    );
  }
  return R.reverse(R.sortBy(row => row.created, latest));
};
